import React, { useRef, useState } from "react";
import { UploadSimple, Scissors, TextAa, Play, Pause, Download, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { debugLog } from "../lib/debug";

export default function Editor() {
  const [video, setVideo] = useState(null); // {id, filename, size, src}
  const [output, setOutput] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(true);
  const [captionText, setCaptionText] = useState("MUST WATCH");
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [vertical, setVertical] = useState(true);
  const videoRef = useRef(null);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/video/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const src = URL.createObjectURL(f);
      setVideo({ id: data.id, filename: f.name, size: f.size, src });
      setOutput(null);
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const process = async () => {
    if (!video) return;
    setProcessing(true); setOutput(null);
    try {
      const { data } = await api.post("/video/process", {
        video_id: video.id,
        trim_start: trimStart,
        trim_end: trimEnd > trimStart ? trimEnd : null,
        caption_text: captionsOn ? captionText : null,
        vertical_9_16: vertical,
      });
      // Poll job status (BackgroundTasks)
      const jobId = data.job_id;
      let attempts = 0;
      const poll = async () => {
        attempts++;
        try {
          const { data: job } = await api.get(`/video/job/${jobId}`);
          if (job.status === "done" && job.output_id) {
            // Fetch short-lived signed URL
            const { data: signed } = await api.get(`/video/${job.output_id}/sign`);
            const streamUrl = `${API}/video/${job.output_id}/stream?sig=${encodeURIComponent(signed.sig)}`;
            setOutput({ id: job.output_id, url: streamUrl, size: job.size });
            setProcessing(false);
            toast.success("Export ready");
            return;
          }
          if (job.status === "failed") {
            toast.error(job.error || "Processing failed");
            setProcessing(false);
            return;
          }
        } catch (e) {
          debugLog("editor poll attempt failed:", e?.message);
        }
        if (attempts < 40) setTimeout(poll, 3000);
        else { setProcessing(false); toast.error("Timed out"); }
      };
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start processing");
      setProcessing(false);
    }
  };

  const toggle = () => {
    if (!videoRef.current) return;
    if (playing) videoRef.current.pause();
    else videoRef.current.play();
    setPlaying(!playing);
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="editor-page">
      <p className="text-[#FF3B6B] text-sm font-mono">// VIDEO EDITOR</p>
      <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2">Vertical 9:16 editor</h1>
      <p className="text-white/50 mt-1">Upload · trim · caption overlay · 9:16 export — FFmpeg powered.</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <div className="bg-[#050505] rounded-xl border border-white/10 aspect-[9/16] max-h-[600px] mx-auto flex items-center justify-center relative overflow-hidden" data-testid="editor-canvas">
            {!video && (
              <label className="cursor-pointer text-center text-white/40 p-8" data-testid="editor-upload-label">
                <UploadSimple size={50} className="mx-auto mb-3 text-white/30" />
                <p className="font-medium text-white/80">{uploading ? "Uploading..." : "Drop or upload a video"}</p>
                <p className="text-xs mt-1">MP4 / MOV up to 100MB</p>
                <input type="file" accept="video/*" className="hidden" onChange={upload} disabled={uploading} data-testid="editor-file-input" />
              </label>
            )}
            {video && (
              <>
                <video ref={videoRef} src={output?.url || video.src} className="w-full h-full object-contain" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} data-testid="editor-video" controls={!!output} />
                {!output && captionsOn && (
                  <div className="absolute bottom-12 left-1/2 -translate-x-1/2 bg-black/80 px-3 py-1.5 rounded text-sm font-bold tracking-tight text-white pointer-events-none">
                    {captionText}
                  </div>
                )}
                {!output && (
                  <button onClick={toggle} className="absolute bottom-4 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full btn-primary flex items-center justify-center" data-testid="editor-play-btn">
                    {playing ? <Pause weight="fill" size={18} /> : <Play weight="fill" size={18} />}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        <div className="lg:col-span-4 space-y-3">
          <div className="glass rounded-xl p-4 space-y-3" data-testid="editor-tools">
            <p className="text-xs text-white/50 uppercase tracking-wider">Trim (seconds)</p>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" value={trimStart} onChange={(e) => setTrimStart(Number(e.target.value))} min={0} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm" placeholder="Start" data-testid="trim-start-input" />
              <input type="number" value={trimEnd} onChange={(e) => setTrimEnd(Number(e.target.value))} min={0} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm" placeholder="End" data-testid="trim-end-input" />
            </div>
            <button onClick={() => setCaptionsOn(!captionsOn)} className={`w-full flex items-center gap-3 p-2.5 rounded-md text-sm ${captionsOn ? "bg-[#FF3B6B]/10 border border-[#FF3B6B]/30" : "bg-white/5"}`} data-testid="toggle-captions-btn">
              <TextAa size={16} className="text-[#00E5FF]" /> Caption overlay {captionsOn ? "(on)" : "(off)"}
            </button>
            {captionsOn && (
              <input type="text" value={captionText} onChange={(e) => setCaptionText(e.target.value.slice(0, 60))} className="w-full bg-white/5 border border-white/10 rounded px-3 py-1.5 text-sm" maxLength={60} data-testid="caption-text-input" />
            )}
            <button onClick={() => setVertical(!vertical)} className={`w-full flex items-center gap-3 p-2.5 rounded-md text-sm ${vertical ? "bg-[#FF3B6B]/10 border border-[#FF3B6B]/30" : "bg-white/5"}`} data-testid="toggle-vertical-btn">
              <Scissors size={16} className="text-[#EAB308]" /> 9:16 crop {vertical ? "(on)" : "(off)"}
            </button>
          </div>
          <button onClick={process} disabled={!video || processing} className="w-full btn-primary py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="export-video-btn">
            {processing ? <><Lightning size={16} weight="fill" className="animate-pulse" /> Processing...</> : <><Download size={16} /> Export HD</>}
          </button>
          {output && (
            <a href={output.url} download className="block w-full text-center bg-white text-black py-2.5 rounded-lg font-medium text-sm" data-testid="download-export-link">
              Download exported MP4
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
